import React from 'react'
import { Box, Cylinder } from '@react-three/drei'
import { PoolModel as PoolModelType } from '../../../store/designStore'

interface PoolModelProps extends PoolModelType {
  onClick?: () => void
  onPointerDown?: (event: any) => void
  isSelected?: boolean
  isDragging?: boolean
}

const PoolModel: React.FC<PoolModelProps> = ({ 
  position, 
  rotation, 
  scale, 
  dimensions, 
  materials, 
  features,
  shape,
  onClick,
  onPointerDown,
  isSelected = false,
  isDragging = false
}) => {
  
  // Generate different pool geometries based on shape
  const renderPoolShape = () => {
    const { width, length, depth } = dimensions
    
    // Debug: Log dimensions to see what we're working with
    console.log(`🏊‍♂️ Pool ${shape} dimensions:`, { width, length, depth })
    
    switch (shape) {
      case 'rectangular':
        return (
          <Box args={[width, depth, length]} receiveShadow>
            <meshStandardMaterial color={materials.water} transparent opacity={0.8} />
          </Box>
        )
      
      case 'oval':
        // Oval pool: Create ellipse by scaling a cylinder to match width and length
        return (
          <Cylinder 
            args={[1, 1, depth, 32]} 
            rotation={[0, 0, 0]} 
            scale={[width / 2, 1, length / 2]}
            receiveShadow
          >
            <meshStandardMaterial color={materials.water} transparent opacity={0.8} />
          </Cylinder>
        )
      
      case 'round':
        // Round pool: Use average of width and length for diameter
        const radius = (width + length) / 4
        return (
          <Cylinder args={[radius, radius, depth, 32]} receiveShadow>
            <meshStandardMaterial color={materials.water} transparent opacity={0.8} />
          </Cylinder>
        )
      
      case 'kidney':
        // Kidney shape: Main body + smaller curved section
        return (
          <group>
            {/* Main body */}
            <Box args={[width * 0.6, depth, length * 0.7]} position={[-width * 0.1, 0, 0]} receiveShadow>
              <meshStandardMaterial color={materials.water} transparent opacity={0.8} />
            </Box>
            {/* Curved section */}
            <Cylinder 
              args={[width * 0.3, width * 0.3, depth, 16]} 
              position={[width * 0.2, 0, length * 0.15]} 
              receiveShadow
            >
              <meshStandardMaterial color={materials.water} transparent opacity={0.8} />
            </Cylinder>
            {/* Connecting section */}
            <Box args={[width * 0.3, depth, length * 0.4]} position={[width * 0.05, 0, -length * 0.1]} receiveShadow>
              <meshStandardMaterial color={materials.water} transparent opacity={0.8} />
            </Box>
          </group>
        )
      
      case 'l-shape':
        // L-shaped pool: Two connected rectangles
        return (
          <group>
            {/* Main section */}
            <Box args={[width, depth, length * 0.6]} position={[0, 0, -length * 0.2]} receiveShadow>
              <meshStandardMaterial color={materials.water} transparent opacity={0.8} />
            </Box>
            {/* Side section */}
            <Box args={[width * 0.6, depth, length * 0.4]} position={[width * 0.2, 0, length * 0.3]} receiveShadow>
              <meshStandardMaterial color={materials.water} transparent opacity={0.8} />
            </Box>
          </group>
        )
      
      case 'freeform':
        // Freeform: Multiple overlapping organic shapes
        return (
          <group>
            {/* Main body */}
            <Cylinder 
              args={[width * 0.4, width * 0.4, depth, 8]} 
              position={[-width * 0.15, 0, -length * 0.1]} 
              receiveShadow
            >
              <meshStandardMaterial color={materials.water} transparent opacity={0.8} />
            </Cylinder>
            {/* Secondary body */}
            <Cylinder 
              args={[width * 0.35, width * 0.35, depth, 8]} 
              position={[width * 0.1, 0, length * 0.05]} 
              receiveShadow
            >
              <meshStandardMaterial color={materials.water} transparent opacity={0.8} />
            </Cylinder>
            {/* Connecting section */}
            <Box args={[width * 0.5, depth, length * 0.3]} position={[0, 0, 0]} receiveShadow>
              <meshStandardMaterial color={materials.water} transparent opacity={0.8} />
            </Box>
            {/* Small accent */}
            <Cylinder 
              args={[width * 0.2, width * 0.2, depth, 6]} 
              position={[width * 0.2, 0, length * 0.25]} 
              receiveShadow
            >
              <meshStandardMaterial color={materials.water} transparent opacity={0.8} />
            </Cylinder>
          </group>
        )
      
      default:
        return (
          <Box args={[width, depth, length]} receiveShadow>
            <meshStandardMaterial color={materials.water} transparent opacity={0.8} />
          </Box>
        )
    }
  }
  return (
    <group 
      position={position} 
      rotation={rotation} 
      scale={isDragging ? [scale[0] * 1.05, scale[1] * 1.05, scale[2] * 1.05] : scale} 
      onClick={onClick}
      onPointerDown={onPointerDown}
    >
      {/* Selection outline */}
      {isSelected && (
        <Box
          args={[dimensions.width + 1, dimensions.depth + 0.5, dimensions.length + 1]}
          position={[0, 0, 0]}
        >
          <meshBasicMaterial 
            color={isDragging ? "#ff6b00" : "#00ff00"} 
            wireframe 
            transparent 
            opacity={isDragging ? 0.7 : 0.5} 
          />
        </Box>
      )}
      
      {/* Main pool water - using shape-specific rendering */}
      <group position={[0, -0.75, 0]}>
        {renderPoolShape()}
      </group>
      
      {/* Pool coping/edge */}
      <Box
        args={[dimensions.width + 1, 0.3, dimensions.length + 1]}
        position={[0, 0.15, 0]}
        castShadow
        receiveShadow
      >
        <meshStandardMaterial color={materials.coping} />
      </Box>
      
      {/* Pool deck */}
      <Box
        args={[dimensions.width + 4, 0.1, dimensions.length + 4]}
        position={[0, 0.05, 0]}
        receiveShadow
      >
        <meshStandardMaterial color={materials.deck} />
      </Box>
      
      {/* Spa (if enabled) */}
      {features.spa && (
        <Cylinder
          args={[2, 2, 1]}
          position={[dimensions.width / 2 + 1, -0.5, dimensions.length / 2 + 1]}
          castShadow
          receiveShadow
        >
          <meshStandardMaterial color={materials.water} transparent opacity={0.8} />
        </Cylinder>
      )}
      
      {/* Pool lights (if enabled) */}
      {features.lighting && (
        <>
          <Cylinder
            args={[0.2, 0.2, 0.1]}
            position={[-dimensions.width / 2 + 1, -0.2, -dimensions.length / 2 + 1]}
          >
            <meshStandardMaterial color="#ffff00" emissive="#ffff00" emissiveIntensity={0.5} />
          </Cylinder>
          <Cylinder
            args={[0.2, 0.2, 0.1]}
            position={[dimensions.width / 2 - 1, -0.2, dimensions.length / 2 - 1]}
          >
            <meshStandardMaterial color="#ffff00" emissive="#ffff00" emissiveIntensity={0.5} />
          </Cylinder>
        </>
      )}
    </group>
  )
}

export default PoolModel